<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { defineProps } from 'vue'
import useTailwindColor from 'Common/utils/useTailwindColor'

interface PropTypes {
  checked: boolean
  color: string
}

const props = defineProps<PropTypes>()

const checkPath = ref(null)
const checkColor = computed(() => {
  return useTailwindColor(props.color)
})

const checkStyle = ref({
  strokeDasharray: 0,
  strokeDashoffset: 0,
  transition: 'stroke-dashoffset 0.5s ease',
})

onMounted(() => {
  if (checkPath.value) {
    const length = checkPath.value.getTotalLength()
    checkStyle.value.strokeDasharray = length
    checkStyle.value.strokeDashoffset = props.checked ? 0 : length
  }
})

watch(
  () => props.checked,
  (newVal) => {
    if (checkPath.value) {
      const length = checkPath.value.getTotalLength()
      checkStyle.value.strokeDasharray = length
      checkStyle.value.strokeDashoffset = newVal ? 0 : length
    }
  }
)
</script>

<template>
  <svg
    width="50"
    class="check-icon w-full h-full"
    height="50"
    viewBox="0 0 50 50"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    role="img"
    :aria-label="checked ? 'Checked' : 'Unchecked'"
  >
    <!-- Circle Background -->
    <circle cx="25" cy="25" r="22" :stroke="checkColor" stroke-width="4" fill="none" />

    <!-- Animated Checkmark -->
    <path
      ref="checkPath"
      d="M15 25 L22 32 L35 18"
      :stroke="checkColor"
      stroke-width="4"
      fill="none"
      stroke-linecap="round"
      stroke-linejoin="round"
      :style="checkStyle"
    />
  </svg>
</template>

<style scoped>
.check-icon {
  display: block; /* Ensures SVG respects parent dimensions */
  cursor: pointer;
}

.check-icon path {
  /* Ensure the checkmark is hidden initially if not checked */
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
}
</style>
